/**
 * Self-contained policy fixtures + value parse/serialize helpers for the
 * hoisted Policies doc block.
 *
 * The app keeps the canonical `POLICY_CATALOG` and the parse/serialize helpers
 * in `apps/yaas-client/src/pages/shared/programMocks.ts` + `programHelpers.ts`
 * (both wired into the wider Programs page). The Policies section only needs a
 * MINIMAL subset — the five catalog templates its dropdown / Suggested cards
 * offer, plus the wallet/tranche value codecs the config controls read — so
 * this file carries a dependency-free copy instead of dragging the whole app
 * graph into the widget bundle. Kept structurally in sync with the app so the
 * app can keep passing its real `Policy[]` straight through the re-export shim.
 */

import type { ConfigurablePolicy, NewUserPolicy, SliderPolicy, TranchePolicy, WalletPolicy } from "./types";

// ─── Wallet-policy config (3-way mode + address list) ────────────────────
export type WalletMode = "whitelist" | "blacklist" | "kyc-only";

export interface WalletConfig {
    /** Whitelist (only listed qualify), Blacklist (all but listed qualify),
     *  or KYC Only (eligibility gated on KYC-approved status — the manual
     *  address list is unused). */
    mode: string;
    addresses: string[];
}

/** Parse a wallet policy's stored value (JSON `{mode, addresses}`), tolerating
 *  the legacy newline-joined-addresses form. Back-compat: an old value carrying
 *  `kycOnly: true` is folded into `mode: "kyc-only"`. */
export function parseWalletConfig(value: number | string | undefined, fallbackMode: string): WalletConfig {
    if (typeof value === "string" && value.trim().startsWith("{")) {
        try {
            const parsed = JSON.parse(value) as Partial<WalletConfig> & {
                kycOnly?: boolean;
            };
            const rawMode = typeof parsed.mode === "string" ? parsed.mode : fallbackMode;
            // Legacy shape: `kycOnly: true` on any mode ⇒ the new KYC-only mode.
            const mode = parsed.kycOnly === true ? "kyc-only" : rawMode;
            return {
                mode,
                addresses: Array.isArray(parsed.addresses)
                    ? parsed.addresses.filter((a): a is string => typeof a === "string")
                    : []
            };
        } catch {
            // fall through to legacy parsing
        }
    }
    const addresses = typeof value === "string" && value ? value.split("\n").filter(Boolean) : [];
    return { mode: fallbackMode, addresses };
}

export function serializeWalletConfig(config: WalletConfig): string {
    return JSON.stringify(config);
}

/** Active description for a wallet policy — the KYC copy in `kyc-only` mode,
 *  else the selected mode's copy when it has modes, else its static
 *  description. */
export function walletDescription(policy: WalletPolicy, value: number | string | undefined): string {
    if (policy.modes && policy.modes.length > 0) {
        const { mode } = parseWalletConfig(value, policy.modes[0].value);
        if (mode === "kyc-only") {
            return "Only KYC-approved wallets qualify.";
        }
        return policy.modes.find((m) => m.value === mode)?.description ?? policy.description;
    }
    return policy.description;
}

// ─── Tranche-policy config (threshold amount + reward multiplier rows) ────
export interface Tranche {
    /** Threshold amount. `null` = the field is empty (not yet typed); the
     *  editor renders it blank rather than seeding a `0`. */
    amount: number | null;
    /** Reward multiplier. `null` = empty (not yet typed). */
    multiplier: number | null;
}

/** Coerce a stored tranche field to a number, preserving EMPTY (`null`). A
 *  missing / non-finite value round-trips as `null` so a freshly-added tranche
 *  row stays blank in the editor instead of coercing to a default `0`. */
function trancheField(raw: unknown): number | null {
    if (raw === null || raw === undefined || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
}

/** Parse a tranche policy's stored value (JSON array of `{amount, multiplier}`).
 *  Empty fields are preserved as `null` (see {@link trancheField}) so the editor
 *  can render a blank, user-fillable row. Read-path consumers coalesce
 *  `null → 0` at the display site. */
export function parseTranches(value: number | string | undefined): Tranche[] {
    if (typeof value === "string" && value.trim().startsWith("[")) {
        try {
            const arr = JSON.parse(value) as unknown[];
            if (Array.isArray(arr)) {
                return arr.map((t) => {
                    const obj = (t ?? {}) as Partial<Record<keyof Tranche, unknown>>;
                    return {
                        amount: trancheField(obj.amount),
                        multiplier: trancheField(obj.multiplier)
                    };
                });
            }
        } catch {
            // fall through
        }
    }
    return [];
}

export function serializeTranches(tranches: Tranche[]): string {
    return JSON.stringify(tranches);
}

// ─── New-User-policy config (one-time signup bonus + recency window) ──────
export interface NewUserConfig {
    /** One-time USDv payout per eligible user. `null` = the field is empty
     *  (not yet typed) — the editor renders it blank instead of seeding a
     *  default. */
    bonusAmount: number | null;
    /** Eligibility window in days — "joined within N days". `null` = empty. */
    joinedWithinDays: number | null;
}

/** Parse a new-user policy's stored value (JSON `{bonusAmount, joinedWithinDays}`).
 *
 *  EMPTY-PRESERVING: a missing / non-finite / explicitly-`null` field stays
 *  `null` (does NOT fall back to a default) so a freshly-chosen policy renders
 *  its bonus + joined-within fields BLANK while the user fills them in. The
 *  read-path (`newUserDescription`, PolicyRow) coalesces `null → 0` at the
 *  display site instead. */
export function parseNewUserConfig(value: number | string | undefined): NewUserConfig {
    if (typeof value === "string" && value.trim().startsWith("{")) {
        try {
            const parsed = JSON.parse(value) as Partial<Record<keyof NewUserConfig, unknown>>;
            const toField = (raw: unknown): number | null => {
                if (raw === null || raw === undefined || raw === "") return null;
                const n = Number(raw);
                return Number.isFinite(n) ? n : null;
            };
            return {
                bonusAmount: toField(parsed.bonusAmount),
                joinedWithinDays: toField(parsed.joinedWithinDays)
            };
        } catch {
            // fall through to empty
        }
    }
    return { bonusAmount: null, joinedWithinDays: null };
}

export function serializeNewUserConfig(config: NewUserConfig): string {
    return JSON.stringify(config);
}

/** Active read-view description for a new-user policy — the one-time bonus
 *  amount + the recency window ("One-time X USDv bonus for users who joined
 *  within N days"). Consume-once semantics are implied (backend-enforced). */
export function newUserDescription(policy: NewUserPolicy, value: number | string | undefined): string {
    const parsed = parseNewUserConfig(value);
    // Read-path: an empty (null) field falls back to the policy default for the
    // summary copy — the editor keeps the field blank, but the committed /
    // summarized description always reads a concrete number.
    const bonusAmount = parsed.bonusAmount ?? policy.defaultBonusAmount;
    const joinedWithinDays = parsed.joinedWithinDays ?? policy.defaultJoinedWithinDays;
    return `One-time ${bonusAmount.toLocaleString(
        "en-US"
    )} USDv bonus for users who joined within ${joinedWithinDays.toLocaleString(
        "en-US"
    )} day${joinedWithinDays === 1 ? "" : "s"}.`;
}

/** Initial editor value for a freshly-chosen policy type — every fillable
 *  numeric field starts EMPTY so the user types it in (no seeded default):
 *   - **slider** (Minimum Threshold): `""` — the amount field renders blank.
 *   - **tranche**: a single COMPLETE base row (`{ amount: 0, multiplier: 1 }`) —
 *     the sensible "everyone at 1×" base tier. The user adds further tiers via
 *     +Add Tranche, and each APPENDED row starts EMPTY (`{ null, null }`) for
 *     the user to fill.
 *   - **new-user**: both bonus + joined-within start `null` (blank).
 *   - **wallet**: unchanged — the mode is a toggle (a selection, not a fill
 *     field) and the address list already starts empty.
 *   - **select**: unchanged — a select has no empty state, so it keeps its
 *     catalog default. */
export function initialValueFor(available: ConfigurablePolicy[], id: string): number | string {
    const next = available.find((p) => p.id === id);
    if (!next) return "";
    if (next.kind === "wallet") {
        return serializeWalletConfig({
            mode: next.modes?.[0]?.value ?? "",
            addresses: []
        });
    }
    if (next.kind === "tranche") {
        // Start from a single COMPLETE base tranche — the sensible "everyone at
        // 1×" base tier (amount 0, multiplier 1). Further tiers are ADDED via
        // +Add Tranche and start EMPTY ({ null, null }) for the user to fill.
        return serializeTranches([{ amount: 0, multiplier: 1 }]);
    }
    if (next.kind === "new-user") {
        // Both fields blank — the user types the bonus + recency window.
        return serializeNewUserConfig({
            bonusAmount: null,
            joinedWithinDays: null
        });
    }
    if (next.kind === "slider") {
        // Empty amount field — the user types the threshold in.
        return "";
    }
    return next.defaultValue;
}

/** Whether a policy's in-progress editor value has every REQUIRED fillable
 *  field filled with a valid non-empty value — the gate for enabling
 *  "Add Policy". Now that config fields start EMPTY (see {@link initialValueFor}),
 *  the editor can't commit until the user types them in:
 *   - **slider** (Minimum Threshold): a non-empty numeric amount (`> 0`).
 *   - **new-user**: BOTH bonus + joined-within filled (non-null, `> 0`).
 *   - **tranche**: at least ONE complete row (amount ≥ 0 AND multiplier > 0).
 *   - **wallet**: always complete — the mode is a selection (a default is
 *     always set) and the address list is optional per mode.
 *   - **select**: always complete — a select always carries a value. */
export function isPolicyValueComplete(policy: ConfigurablePolicy, value: number | string): boolean {
    if (policy.kind === "slider") {
        if (value === "" || value === null || value === undefined) return false;
        const n = Number(value);
        return Number.isFinite(n) && n > 0;
    }
    if (policy.kind === "new-user") {
        const { bonusAmount, joinedWithinDays } = parseNewUserConfig(value);
        return bonusAmount !== null && bonusAmount > 0 && joinedWithinDays !== null && joinedWithinDays > 0;
    }
    if (policy.kind === "tranche") {
        const tranches = parseTranches(value);
        // At least one fully-specified row (amount present & ≥ 0, multiplier
        // present & > 0). A base row at amount 0 is legitimate (the "everyone"
        // tier), so amount only needs to be non-empty, not > 0.
        return tranches.some((t) => t.amount !== null && t.amount >= 0 && t.multiplier !== null && t.multiplier > 0);
    }
    // wallet / select — always have a committable value.
    return true;
}

// ─── Catalog subset ──────────────────────────────────────────────────────
// The five configurable policy templates the Policies section's dropdown +
// Suggested cards offer. Verbatim from the app `programMocks` POLICY_CATALOG.

const MINIMUM_THRESHOLD: SliderPolicy = {
    kind: "slider",
    id: "minimum-threshold",
    title: "Minimum Threshold",
    describe: () => "Require a minimum balance to qualify.",
    min: 0,
    max: 10000,
    step: 100,
    defaultValue: 1000,
    unit: "usdpt"
};

const TIME_HELD_TRANCHE: TranchePolicy = {
    kind: "tranche",
    id: "time-held-tranche",
    title: "Time Held Tranche",
    description: "Create tranches for different amount of time held.",
    amountUnit: "days",
    defaultTranches: [
        { amount: 0, multiplier: 1 },
        { amount: 30, multiplier: 1.25 },
        { amount: 90, multiplier: 1.5 }
    ]
};

const BALANCE_AMOUNT_TRANCHE: TranchePolicy = {
    kind: "tranche",
    id: "balance-amount-tranche",
    title: "Balance Amount Tranche",
    description: "Create tranches for different balance amounts.",
    amountUnit: "usdpt",
    defaultTranches: [
        { amount: 0, multiplier: 1 },
        { amount: 10000, multiplier: 1.5 },
        { amount: 50000, multiplier: 2 }
    ]
};

const WALLET_LIST: WalletPolicy = {
    kind: "wallet",
    id: "wallet-list",
    title: "Specific Access Control",
    description: "Restrict eligibility by wallet address",
    placeholder: "Paste a wallet address",
    modes: [
        {
            value: "whitelist",
            label: "Whitelist",
            description: "Only allow whitelisted wallets to qualify."
        },
        {
            value: "blacklist",
            label: "Blacklist",
            description: "Prevent blacklisted wallets to qualify."
        },
        {
            value: "kyc-only",
            label: "KYC Only",
            description: "Only KYC-approved wallets qualify."
        }
    ]
};

/** New User policy defaults — exported so sims can seed the editor at the
 *  defaults (and decide whether a field needs typing or is already correct). */
export const NEW_USER_DEFAULT_BONUS_AMOUNT = 25;
export const NEW_USER_DEFAULT_JOINED_WITHIN_DAYS = 30;

const NEW_USER: NewUserPolicy = {
    kind: "new-user",
    id: "new-user",
    title: "New User",
    description: "One-time bonus for customers who recently joined.",
    defaultBonusAmount: NEW_USER_DEFAULT_BONUS_AMOUNT,
    defaultJoinedWithinDays: NEW_USER_DEFAULT_JOINED_WITHIN_DAYS
};

export const POLICY_CATALOG: ConfigurablePolicy[] = [
    MINIMUM_THRESHOLD,
    TIME_HELD_TRANCHE,
    BALANCE_AMOUNT_TRANCHE,
    WALLET_LIST,
    NEW_USER
];
